/**
 * Fits a chosen logo inside 800x800 (aspect kept, never enlarged) before it is
 * sent: radaccess takes a PNG or JPEG of at most 150 KB, 16-1600 px a side.
 * PNG first, so a transparent logo stays transparent on the invoice; a PNG that
 * is still too large goes as a JPEG on white. Browser only: canvas + Image.
 */
export const LOGO_MAX_SIDE = 800;
export const LOGO_MAX_BYTES = 150 * 1024;

export function fitInside(w: number, h: number, max = LOGO_MAX_SIDE): { w: number; h: number } {
  const scale = Math.min(1, max / Math.max(w, h));
  return { w: Math.max(1, Math.round(w * scale)), h: Math.max(1, Math.round(h * scale)) };
}

/** Bytes behind a base64 data URL. */
export function dataUrlBytes(dataUrl: string): number {
  const b64 = dataUrl.slice(dataUrl.indexOf(",") + 1);
  return Math.floor((b64.length * 3) / 4) - (b64.endsWith("==") ? 2 : b64.endsWith("=") ? 1 : 0);
}

export async function shrinkLogo(file: File): Promise<string> {
  if (!/^image\//.test(file.type)) throw new Error("not-an-image");
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error("not-an-image"));
      i.src = url;
    });
    if (img.naturalWidth < 16 || img.naturalHeight < 16) throw new Error("too-small");
    const { w, h } = fitInside(img.naturalWidth, img.naturalHeight);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("not-an-image");
    ctx.drawImage(img, 0, 0, w, h);
    const png = canvas.toDataURL("image/png");
    if (dataUrlBytes(png) <= LOGO_MAX_BYTES) return png;
    ctx.globalCompositeOperation = "destination-over"; // white behind, not black, as JPEG
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    return canvas.toDataURL("image/jpeg", 0.85);
  } finally {
    URL.revokeObjectURL(url);
  }
}
